import * as React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Card,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import logo1 from "../src/assests/images/logo1.png";
import { useIatfContext } from "./Usecontext";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const Navbar = ({ onAddDocument }) => {
  const theme = useTheme();
  const { loginDetails, setLoginDetails } = useIatfContext();

  const [animateContent, setAnimateContent] = React.useState(false);
  const [logoAnimate, setLogoAnimate] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    React.useState(null);

  // Get user data from context
  const userData = loginDetails || {};
  const userName =
    userData.userId || userData.username || userData.name || "Guest User";

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateContent(true);
    }, 200);
    const logoTimer = setTimeout(() => {
      setLogoAnimate(true);
    }, 100);
    return () => {
      clearTimeout(timer);
      clearTimeout(logoTimer);
    };
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsMenuOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationsAnchorEl(null);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "http://10.200.20.4/IATF_files/IATF_User_Manuls.pdf";
    link.target = "_blank";
    link.download = "IATF_UserManual.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    setLoginDetails({});
    localStorage.clear();
    window.location.href = "/";
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Disable right-click
  React.useEffect(() => {
    const disableRightClick = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", disableRightClick);
    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          backgroundImage: "linear-gradient(95deg, #2D68C4, #0CAFFF)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
          background: "linear-gradient(135deg, #2D68C4 0%, #0CAFFF 100%)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            padding: { xs: "8px 16px", sm: "12px 24px" },
            minHeight: { xs: 64, sm: 72 },
          }}
        >
          {/* Left Section: Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flex: 1,
            }}
          >
            <Box
              sx={{
                transform: logoAnimate ? "translateX(0)" : "translateX(-50px)",
                opacity: logoAnimate ? 1 : 0,
                transition: "all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
              }}
            >
              <img
                src={logo1}
                alt="Logo"
                height={45}
                width={160}
                style={{
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s ease",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                }}
              />
            </Box>

            {/* Center: App Title */}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1, // Takes up available space to center
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                  letterSpacing: "1px",
                  background:
                    "linear-gradient(135deg, #FFFFFF 0%, #E3F2FD 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 2px 10px rgba(255, 255, 255, 0.2)",
                  textAlign: "center",
                }}
              >
                MFA
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  letterSpacing: "0.5px",
                  textAlign: "center",
                }}
              >
                Multi-Factor Authentication
              </Typography>
            </Box>
          </Box>

          {/* Right Section: User Info & Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            {/* User Profile Card */}
            <Card
              onClick={handleProfileMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: { xs: "6px 12px", sm: "8px 16px" },
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "12px",
                cursor: "pointer",
                minWidth: { xs: 140, sm: 180 },
                gap: 1.5,
                transform: animateContent
                  ? "translateX(0)"
                  : "translateX(50px)",
                opacity: animateContent ? 1 : 0,
                transition: "all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              {/* Replace the entire Avatar component with: */}
              <PersonIcon
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: theme.palette.secondary.main,
                  color: "white",
                  borderRadius: "50%",
                  padding: "8px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "white",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    display: "block",
                    lineHeight: 1.2,
                  }}
                >
                  {userData.role || userData.department || "User Account"}
                </Typography>
              </Box>

              <ArrowDropDownIcon
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  transition: "transform 0.3s ease",
                  transform: anchorEl ? "rotate(180deg)" : "rotate(0)",
                }}
              />
            </Card>
          </Box>

          {/* Profile Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 4,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                "& .MuiMenuItem-root": {
                  py: 1.5,
                  px: 2,
                },
              },
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <PersonIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
              <Typography variant="body2">My Profile</Typography>
            </MenuItem>

            <Divider />
            <MenuItem onClick={handleLogout}>
              <ExitToAppIcon sx={{ mr: 2, color: theme.palette.error.main }} />
              <Typography variant="body2" color="error">
                Logout
              </Typography>
            </MenuItem>
          </Menu>

          {/* Notifications Dropdown Menu */}
          <Menu
            anchorEl={notificationsAnchorEl}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 300,
                maxHeight: 400,
                borderRadius: "12px",
                overflow: "hidden",
              },
            }}
          >
            <MenuItem dense sx={{ justifyContent: "center", py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Notifications
              </Typography>
            </MenuItem>
            <Divider />
            {[1, 2, 3].map((item) => (
              <MenuItem key={item} dense sx={{ py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <NotificationsIcon
                    sx={{ fontSize: 20, color: theme.palette.primary.main }}
                  />
                  <Box>
                    <Typography variant="body2">New login detected</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Just now
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Navbar;
